import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import {
  FederatedSignInOptions,
  FederatedSignInOptionsCustom,
} from '@aws-amplify/auth/lib-esm/types/Auth';
import { API, Auth } from 'aws-amplify';
import axios from 'axios';
import * as Yup from 'yup';

import {
  ADD_ACCOUNT_PAGE,
  ADMIN_PAGE,
  AppViewMode,
  AUTH_ACCESS_LINK_PAGE,
  EmailValidationRegex,
  LOGIN_PAGE_BASE_PATH,
  MCP_LOGIN_PAGE,
  ONBOARDING_PAGE,
  QA_HARNESS_PAGE,
  QA_HARNESS_RUN_TOKEN_PARAM,
  SIGNUP_PAGE,
  UnAuthAPIName,
  WORKSPACES_PAGE,
} from 'src/constants';
import {
  REDIRECT_PATH_AFTER_GOOGLE_LOGIN,
  SIGNED_IN_WITH_GOOGLE_USER_KEY,
} from 'src/constants/authConsts';
import { PortalConfig } from 'src/context';
import {
  isElectronApp,
  openDesktopOAuthUrl,
  openWebOAuthUrl,
} from 'src/utils/ElectronAuthUtils';
import { withPosthogSession } from 'src/utils/posthogSession';

const unAuthPageRoutes = [
  LOGIN_PAGE_BASE_PATH,
  AUTH_ACCESS_LINK_PAGE,
  ADD_ACCOUNT_PAGE,
  WORKSPACES_PAGE,
  SIGNUP_PAGE,
  ONBOARDING_PAGE,
  ADMIN_PAGE,
  MCP_LOGIN_PAGE,
];

const flexiblyAuthedPageRoutes = ['/payment-links/pay', '/store'];

// Unlike flexiblyAuthedPageRoutes (always public), these routes have a normal
// Cognito-authed interactive use too, so they're only flexibly authed when the
// request actually carries the run-scoped token — a bare hit on the path still
// requires a real session, exactly as it does today.
const tokenGatedFlexiblyAuthedPageRoutes = [QA_HARNESS_PAGE];

type AuthConfigInput = PortalConfig['AWS']['Auth'];

export const isLocalstackAuthEndpoint = (endpoint?: string) =>
  Boolean(endpoint && endpoint.includes('/api/localstack-cognito'));

export const getAmplifyAuthConfig = ({
  baseAuthConfig,
  authenticationFlowType,
}: {
  baseAuthConfig: AuthConfigInput;
  authenticationFlowType: string;
}) => {
  if (isLocalstackAuthEndpoint(baseAuthConfig.endpoint)) {
    const { identityPoolId: _identityPoolId, ...rest } = baseAuthConfig;
    return {
      ...rest,
      authenticationFlowType,
    };
  }

  return {
    ...baseAuthConfig,
    authenticationFlowType,
  };
};

export const isPassword = Yup.string()
  .max(256, 'Password must be less than 256 characters')
  .min(8, 'Password must be at least 8 characters')
  .trim('Password cannot have leading or trailing spaces')
  .matches(/[a-z]/, 'Password must have at least one lowercase char')
  .matches(/[A-Z]/, 'Password must have at least one uppercase char')
  .matches(/[0-9]+/, 'Password must have at least one number');
export const PasswordInputValidation = isPassword.required(
  'Password is required',
);

export const isValidEmail = (email: string) => EmailValidationRegex.test(email);

/**
 * Mapping of email domains to their Cognito identity provider names.
 * Users with these domains will be redirected to SSO instead of entering a password.
 */
export const IDENTITY_PROVIDER_DOMAINS: Record<string, string> = {
  'capitalone.com': 'capitalone',
};

/**
 * Check if an email address belongs to an identity provider domain.
 * @param email - The email address to check
 * @returns true if the email domain is an identity provider domain
 */
export const isIdentityProviderDomain = (email: string): boolean => {
  if (!email || !isValidEmail(email)) {
    return false;
  }
  const domain = email.split('@')[1]?.toLowerCase();
  return domain in IDENTITY_PROVIDER_DOMAINS;
};

/**
 * Get the identity provider name for an email address.
 * @param email - The email address to check
 * @returns The provider name if the email is from an IDP domain, undefined otherwise
 */
export const getIdentityProvider = (email: string): string | undefined => {
  if (!email || !isValidEmail(email)) {
    return undefined;
  }
  const domain = email.split('@')[1]?.toLowerCase();
  return IDENTITY_PROVIDER_DOMAINS[domain];
};

type GetIdentityProviderForPathInput = {
  email: string;
  pathname: string;
};

/**
 * Resolve the identity provider for a login route.
 * Admin proxy logins must stay on the custom proxy flow even when the
 * entered email matches an SSO-enabled domain.
 */
export const getIdentityProviderForPath = ({
  email,
  pathname,
}: GetIdentityProviderForPathInput): string | undefined => {
  if (pathname.startsWith(ADMIN_PAGE)) {
    return undefined;
  }

  return getIdentityProvider(email);
};

type getPasswordValidationOptions = {
  isPortalLogin: boolean;
  isAdminLogin: boolean;
};

/**
 * Get password validation schema based on login type
 * @param options config for password validation
 * @returns
 */
export const GetPasswordValidation = ({
  isPortalLogin,
  isAdminLogin,
}: getPasswordValidationOptions) => {
  if (isAdminLogin) {
    // admin login does not require password
    return Yup.string();
  }

  if (isPortalLogin) {
    // portal login requires password unless using identity provider
    // Use Yup.when to dynamically validate based on email domain
    return Yup.string().when('email', {
      is: (email: string) => isIdentityProviderDomain(email),
      then: (schema: Yup.StringSchema) => schema, // no password required for IDP domains
      otherwise: (schema: Yup.StringSchema) =>
        schema.required('Password is required'),
    });
  }

  // otherwise this is new password validation where FE checks for regex
  // for nicer validation experience that doesn't require user to submit
  return PasswordInputValidation;
};

type getPreferredUsernameInput = {
  email: string;
  viewMode: string;
  portalId: string;
};

export const getPreferredUsername = ({
  email: inputEmail,
  viewMode,
  portalId,
}: getPreferredUsernameInput) => {
  const email = inputEmail.toLowerCase(); // lower-case email to match saved user property
  return viewMode === 'client' ? `${portalId}|${email}` : email;
};

export type GetSignInMetadataInput = {
  poolId?: string;
  clientId?: string;
};

export type AuthRequestInfo = {
  ip?: string;
  userAgent?: string;
};

/**
 * Fetches the caller's IP address and User-Agent from the backend.
 * Called before login so these values can be forwarded to Cognito
 * via ClientMetadata (Lambda triggers don't have HTTP request context).
 */
export const getAuthRequestInfo = async (): Promise<AuthRequestInfo> => {
  try {
    const response = await API.get(UnAuthAPIName, '/auth/request-info', {
      headers: withPosthogSession({}),
    });
    return {
      ip: response?.ip,
      userAgent: response?.userAgent,
    };
  } catch {
    // If the request fails, fall back to browser-only info
    return {};
  }
};

/**
 * Returns client metadata to attach to every Cognito signIn call.
 * Always includes the browser's User-Agent so the pre-token Lambda
 * trigger can forward it to the audit-log pipeline.
 * Optionally includes IP from the /auth/request-info endpoint and
 * poolId/clientId when present (used for pool migration).
 */
export const GetSignInMetadata = (
  queryParam?: GetSignInMetadataInput,
  requestInfo?: AuthRequestInfo,
): Record<string, string> => {
  const metadata: Record<string, string> = {
    userAgent: requestInfo?.userAgent || navigator.userAgent,
  };
  if (requestInfo?.ip) {
    metadata.ip = requestInfo.ip;
  }
  const poolId = queryParam?.poolId;
  const clientId = queryParam?.clientId;
  if (poolId && clientId) {
    metadata.userPoolId = poolId;
    metadata.clientId = clientId;
  }
  // Flag /admin logins so the pre-token trigger can tag the userLoginSucceeded audit
  // event as super-admin-triggered. Backend also re-checks user.IsInternal() before honoring.
  // Prefix match so `/admin/`, `/admin/login`, etc. are also covered.
  if (
    typeof window !== 'undefined' &&
    window.location?.pathname.startsWith('/admin')
  ) {
    metadata.isAdminLogin = 'true';
  }
  return metadata;
};

/**
 * Check if the pathname starts with any of the UnAuth page routes. This is used by server
 * side rendering and client side rendering to determine which App component to use.
 * @param pathname path name of the request
 * @param query query object of the request
 * @returns true/false if the request is for an unauth page
 */
export const UseUnAuthApp = (pathname: string) =>
  unAuthPageRoutes.some((x) => pathname.startsWith(x));

// True only for a token-gated route actually carrying its run-scoped token.
// SessionMiddleware keys its narrow no-session bypass on this predicate alone,
// so the always-public flexiblyAuthedPageRoutes keep today's dashboard
// behavior exactly.
export const UseTokenGatedFlexibleAuth = (
  pathname: string,
  query?: Record<string, unknown>,
) =>
  tokenGatedFlexiblyAuthedPageRoutes.some((x) => pathname.startsWith(x)) &&
  typeof query?.[QA_HARNESS_RUN_TOKEN_PARAM] === 'string' &&
  query[QA_HARNESS_RUN_TOKEN_PARAM] !== '';

export const UseFlexibleAuth = (
  pathname: string,
  query?: Record<string, unknown>,
) =>
  flexiblyAuthedPageRoutes.some((x) => pathname.startsWith(x)) ||
  UseTokenGatedFlexibleAuth(pathname, query);

/**
 * Set Amplify's oauth.urlOpener so we can back up PKCE/state to localStorage
 * before the Hosted UI redirect. Electron opens the system browser; web navigates
 * in-tab after the same backup.
 */
export const configureDesktopOAuthUrlOpener = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const oauthConfig = window.App?.portalConfig?.AWS.Auth.oauth;
  if (!oauthConfig) {
    return undefined;
  }

  oauthConfig.urlOpener = isElectronApp()
    ? openDesktopOAuthUrl
    : openWebOAuthUrl;
  return oauthConfig.urlOpener;
};

/**
 * Prepare for federated sign-in by saving the redirect path and
 * setting the origin cookie for client portals. Shared by both
 * Google and custom OIDC identity provider sign-in flows.
 */
const prepareFederatedSignIn = async () => {
  const { portalConfig } = window.App;
  const redirectSignOutURL = portalConfig.AWS.Auth.oauth?.redirectSignOut;

  configureDesktopOAuthUrlOpener();

  // The redirect params are lost during federated sign-in, so save them
  // in sessionStorage before redirecting
  const query = new URLSearchParams(window.location.search);
  const next = query.get('next');
  if (next) {
    window.sessionStorage.setItem(REDIRECT_PATH_AFTER_GOOGLE_LOGIN, next);
  }

  // For clients, set the current origin as a cookie so users are redirected
  // back to this url even when the login fails
  if (portalConfig.viewMode === AppViewMode.CLIENT && redirectSignOutURL) {
    const initLoginApi = `${
      new URL(redirectSignOutURL).origin
    }/auth/google/initiate-login?origin=${window.location.origin}`;
    await axios.get(initLoginApi, { withCredentials: true });
  }
};

/**
 * Initiate federated sign-in with Cognito Hosted UI.
 * This is used for both Google and custom OIDC identity provider sign-in flows.
 */
const federatedSignIn = async ({
  customState,
  ...options
}: FederatedSignInOptionsCustom | FederatedSignInOptions): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  await prepareFederatedSignIn();

  const isDesktopApp = isElectronApp();

  const parsed: FederatedSignInOptionsCustom | FederatedSignInOptions = {
    ...options,
    // Tag desktop app sign-ins so the OAuth callback knows to redirect via assembly:// protocol
    customState: isDesktopApp
      ? [customState, 'desktop=true'].filter(Boolean).join('&')
      : customState,
  };

  // this type-casting was a last resort, it really hates any way I try to
  // type it properly. it's type-safe above so should be okay.
  await Auth.federatedSignIn(parsed as FederatedSignInOptions);
};

/**
 * Initiate federated sign-in with Google as the identity provider.
 */
export const SignInWithGoogle = (customState: string) =>
  federatedSignIn({
    provider: CognitoHostedUIIdentityProvider.Google,
    customState,
  });

/**
 * Initiate federated sign-in with a custom OIDC identity provider.
 * Used for SSO login when the user's email domain matches an IDP.
 * @param identityProvider - The Cognito provider name (e.g., 'capitalone')
 * @param customState - Encoded state string with portalId and origin for redirect
 */
export const SignInWithIdentityProvider = (
  identityProvider: string,
  customState: string,
) => federatedSignIn({ customProvider: identityProvider, customState });

/**
 * logs out the current logged in user from cognito's hosted ui
 * @param portalConfig uses this to determine the client id and hosted ui domain to sign out from
 * @param logoutUri this is where the user will be redirected after the logout is complete. This
 * should be one of the allowed log out uris in cognito
 */
export const logoutFromHostedUi = (
  portalConfig: PortalConfig,
  logoutUri: string,
) => {
  // inputs for logout endpoint
  const logOutParams = new URLSearchParams({
    client_id: portalConfig.AWS.Auth.userPoolWebClientId,
    // once log out is complete, we redirect to the url in the current flow
    logout_uri: logoutUri,
  });

  window.localStorage.removeItem(SIGNED_IN_WITH_GOOGLE_USER_KEY);
  // call the logout endpoint of the hosted ui which logs any google authed user out
  // this cannot be used as an xmlhttprequest as with all hosted ui endpoint
  // amplify does the same for all calls, since these redirects are fast its not noticeable in the ui
  window.location.href = `https://${
    portalConfig.AWS.Auth.oauth?.domain
  }/logout?${logOutParams.toString()}`;
};

export const IsLoggedIn = async () => {
  try {
    await Auth.currentSession();
    return true;
  } catch (error) {
    return false;
  }
};
